import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-BG6MEPCE.js";

// src/toc.tsx
import {
  createContext,
  forwardRef,
  useContext,
  useEffect as useEffect2,
  useMemo,
  useRef
} from "react";
import scrollIntoView from "scroll-into-view-if-needed";

// src/merge-refs.ts
function mergeRefs(...refs) {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref !== null) {
        ref.current = value;
      }
    });
  };
}

// src/utils/use-anchor-observer.ts
import { useEffect, useState } from "react";
function useAnchorObserver(watch) {
  const [activeAnchor, setActiveAnchor] = useState();
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setActiveAnchor((f) => {
          for (const entry of entries) {
            const aboveHalf = window.innerHeight / 2 > entry.boundingClientRect.y;
            const active = aboveHalf && entry.isIntersecting;
            if (active) {
              return entry.target.id;
            }
          }
          return f != null ? f : watch[0];
        });
      },
      { rootMargin: `0% 0% -80% 0%` }
    );
    for (const heading of watch) {
      const element = document.getElementById(heading);
      if (element !== null) {
        observer.observe(element);
      }
    }
    return () => {
      observer.disconnect();
    };
  }, [watch]);
  return activeAnchor;
}

// src/toc.tsx
import { jsx } from "react/jsx-runtime";
var ActiveAnchorContext = createContext({
  activeAnchor: void 0,
  containerRef: { current: null }
});
var useActiveAnchor = (url) => {
  const { activeAnchor } = useContext(ActiveAnchorContext);
  return activeAnchor === url.split("#")[1];
};
var TOCProvider = forwardRef(
  (_a, ref) => {
    var _b = _a, { toc } = _b, props = __objRest(_b, ["toc"]);
    const headings = useMemo(() => {
      return toc.map((item) => item.url.split("#")[1]);
    }, [toc]);
    const containerRef = useRef(null);
    const mergedRef = mergeRefs(containerRef, ref);
    const activeAnchor = useAnchorObserver(headings);
    return /* @__PURE__ */ jsx("div", __spreadProps(__spreadValues({ ref: mergedRef }, props), { children: /* @__PURE__ */ jsx(ActiveAnchorContext.Provider, { value: { containerRef, activeAnchor }, children: props.children }) }));
  }
);
TOCProvider.displayName = "TOCProvider";
var TOCItem = forwardRef(
  (props, ref) => {
    const { containerRef } = useContext(ActiveAnchorContext);
    const active = useActiveAnchor(props.href);
    const anchorRef = useRef(null);
    const mergedRef = mergeRefs(anchorRef, ref);
    useEffect2(() => {
      const element = anchorRef.current;
      if (active && element) {
        scrollIntoView(element, {
          behavior: "smooth",
          block: "center",
          inline: "center",
          scrollMode: "always",
          boundary: containerRef.current
        });
      }
    }, [active, containerRef]);
    return /* @__PURE__ */ jsx("a", __spreadProps(__spreadValues({ ref: mergedRef, "data-active": active }, props), { children: props.children }));
  }
);
TOCItem.displayName = "TOCItem";
export {
  TOCItem,
  TOCProvider,
  useActiveAnchor
};
